import type { AxiosInstance } from 'axios'

/**
 * Silent session renewal (ADR-0019). The access token in `sm-auth` lives 15
 * minutes; the API keeps a 30-day refresh token in an HttpOnly cookie scoped
 * to its /auth routes. When any call answers 401, one refresh is made (shared
 * by every caller that hits 401 in the meantime), the stored token is
 * replaced and the failed call is retried once. When the refresh itself is
 * refused the session is over: storage is cleared and the user is sent to
 * the login page (or the kiosk's PIN screen).
 */

/** Paths whose 401 means "wrong credentials", never "expired token". */
const NO_REFRESH_PATHS = [
  '/auth',
  '/auth/refresh',
  '/auth/logout',
  '/auth/pin-login',
  '/auth/change-password',
  '/auth/register',
  '/auth/forgot-password',
]

const apiPathOf = (url: string | undefined, apiUrl: string): string | null => {
  if (!url || !url.startsWith(apiUrl)) return null
  const rest = url.slice(apiUrl.length)
  const query = rest.indexOf('?')
  return query >= 0 ? rest.slice(0, query) : rest
}

/** True for the API's auth routes: the only calls that must carry the refresh cookie. */
export function isAuthRequest(url: string | undefined, apiUrl: string): boolean {
  const path = apiPathOf(url, apiUrl)
  return path === '/auth' || (path !== null && path.startsWith('/auth/'))
}

/** A first 401 from an API call that is not itself a credential check. */
export function canRefreshAfter(error: any, apiUrl: string): boolean {
  if (!error || !error.response || error.response.status !== 401 || !error.config) return false
  if (error.config._sessionRetry) return false
  const path = apiPathOf(error.config.url, apiUrl)
  if (path === null) return false
  return !NO_REFRESH_PATHS.includes(path)
}

/** Where a user goes once the session cannot be renewed. */
export function sessionLostPath(pathname: string): string {
  return pathname === '/kiosk' || pathname.startsWith('/kiosk/') ? '/kiosk' : '/auth/login'
}

type RefresherDeps = {
  /** Calls POST /auth/refresh and resolves the new bearer token. */
  refresh: () => Promise<string>
  getAuth: () => any
  setAuth: (auth: any) => void
  removeAuth: () => void
}

/**
 * One refresh at a time: concurrent callers share the in-flight request. A
 * refused refresh clears the stored session and resolves null; the next call
 * tries again from scratch.
 */
export function createSessionRefresher({ refresh, getAuth, setAuth, removeAuth }: RefresherDeps) {
  let inFlight: Promise<string | null> | null = null
  return (): Promise<string | null> => {
    if (inFlight) return inFlight
    inFlight = (async () => {
      try {
        const token = await refresh()
        if (!token) throw new Error('empty refresh response')
        setAuth({ ...(getAuth() || {}), api_token: token })
        return token
      } catch {
        removeAuth()
        return null
      } finally {
        inFlight = null
      }
    })()
    return inFlight
  }
}

type InstallOptions = {
  apiUrl: string
  refreshSession: () => Promise<string | null>
  onSessionLost: () => void
}

export function installSessionRefresh(axios: AxiosInstance, { apiUrl, refreshSession, onSessionLost }: InstallOptions) {
  axios.interceptors.request.use((config: any) => {
    if (isAuthRequest(config.url, apiUrl)) config.withCredentials = true
    return config
  })

  axios.interceptors.response.use(
    (response) => response,
    async (error: any) => {
      if (!canRefreshAfter(error, apiUrl)) return Promise.reject(error)
      const token = await refreshSession()
      if (!token) {
        onSessionLost()
        return Promise.reject(error)
      }
      const config = {
        ...error.config,
        _sessionRetry: true,
        headers: { ...(error.config.headers || {}), Authorization: token },
      }
      return axios.request(config)
    }
  )
}
